// src/routes/ProtectedRoute.jsx
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import {
  syncAuthFromLocalStorage,
  selectIsAuthenticated,
  selectIsAdmin,
  selectIsInitialized,
  selectToken,
  expireSession,
  isTokenExpired
} from '../store/slices/authSlice';
import { CircularProgress, Box } from '@mui/material';

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAdmin = useSelector(selectIsAdmin);
  const initialized = useSelector(selectIsInitialized);
  const token = useSelector(selectToken);

  // При първо зареждане — синхронизираме Redux със localStorage
  useEffect(() => {
    if (!initialized) {
      dispatch(syncAuthFromLocalStorage());
    }
  }, [dispatch, initialized]);

  // При всяка навигация (смяна на location) — проверяваме токена локално.
  // Ако е изтекъл, изчистваме сесията без заявка към сървъра.
  // Това е бързо и не товари backend-а.
  useEffect(() => {
    if (initialized && isAuthenticated && isTokenExpired(token)) {
      console.warn('Token expired during navigation, clearing session.');
      dispatch(expireSession());
    }
  }, [location, initialized, isAuthenticated, token, dispatch]);

  // Показваме loader докато не сме инициализирали auth state
  if (!initialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Ако няма автентикация (или токенът tocque е изтекъл и expireSession е dispatch-нат),
  // редиректваме към login
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Ако изисква админ права и потребителят не е админ
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;